/**
 * IndexedDB-backed LocalStore, shared by the web AND desktop clients (008 T016;
 * moved into the platform package so the Tauri desktop app — a WebView, which fully
 * supports IndexedDB — gets the same persistence as web instead of silently falling
 * back to an in-memory store that's wiped on every restart).
 * Backs the persistent queue + telemetry buffer + key-value caches. Falls back
 * to the in-memory store when IndexedDB is unavailable (e.g. private mode/tests).
 */
import { createMemoryLocalStore } from "./memory-store.js";
import type { KeyValueStore, LocalStore, RecordStore } from "../platform-io.js";

const DB_NAME = "genz";
const DB_VERSION = 1;
const KV_STORE = "kv";
const REC_STORE = "records";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KV_STORE)) db.createObjectStore(KV_STORE);
      if (!db.objectStoreNames.contains(REC_STORE)) {
        db.createObjectStore(REC_STORE, { keyPath: "_k" });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgrading the schema would otherwise leave THIS connection open and
      // block it forever (that tab's `open()` hangs on `onblocked`). Close on request.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    // An open blocked by another tab's still-open connection never settles otherwise: every
    // await on `dbp` hangs, so the whole app silently stops persisting. Fail fast instead —
    // `createIdbLocalStore` falls back to the memory store on rejection.
    req.onblocked = () =>
      reject(new Error("IndexedDB open blocked by another tab holding an older version open"));
  });
}

function tx<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    if (mode === "readwrite") {
      // A WRITE must resolve on transaction COMMIT, not request success: `req.onsuccess`
      // fires before the commit, so a QuotaExceededError/abort at commit time was never
      // observed — `put()` "succeeded" while nothing persisted (queue jobs, saveDir, the
      // device fingerprint, synced templates all silently vanished on the next boot).
      let result: T;
      req.onsuccess = () => { result = req.result; };
      req.onerror = () => reject(req.error);
      t.oncomplete = () => resolve(result!);
      t.onabort = () => reject(t.error ?? new Error("IndexedDB transaction aborted (quota?)"));
      t.onerror = () => reject(t.error ?? req.error ?? new Error("IndexedDB transaction failed"));
    } else {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }
  });
}

class IdbKeyValueStore implements KeyValueStore {
  constructor(
    private readonly dbp: Promise<IDBDatabase | null>,
    private readonly fallback: KeyValueStore,
  ) {}
  async get<T = unknown>(key: string): Promise<T | undefined> {
    const db = await this.dbp;
    if (!db) return this.fallback.get<T>(key);
    return tx<T>(db, KV_STORE, "readonly", (s) => s.get(key));
  }
  async set<T = unknown>(key: string, value: T): Promise<void> {
    const db = await this.dbp;
    if (!db) return this.fallback.set(key, value);
    await tx(db, KV_STORE, "readwrite", (s) => s.put(value as unknown as never, key));
  }
  async delete(key: string): Promise<void> {
    const db = await this.dbp;
    if (!db) return this.fallback.delete(key);
    await tx(db, KV_STORE, "readwrite", (s) => s.delete(key));
  }
  async keys(prefix?: string): Promise<string[]> {
    const db = await this.dbp;
    if (!db) return this.fallback.keys(prefix);
    // Range-scoped when a prefix is given: no reason to pull every key into memory.
    const all = (await tx<IDBValidKey[]>(db, KV_STORE, "readonly", (s) =>
      prefix ? s.getAllKeys(prefixRange(prefix)) : s.getAllKeys(),
    )) as string[];
    return all;
  }
}

/** Keys sort lexicographically, so every key starting with `prefix` lies between `prefix`
 *  and `prefix + ￿` — the standard IndexedDB prefix scan. */
function prefixRange(prefix: string): IDBKeyRange {
  return IDBKeyRange.bound(prefix, `${prefix}￿`, false, false);
}

class IdbRecordStore implements RecordStore {
  constructor(
    private readonly dbp: Promise<IDBDatabase | null>,
    private readonly fallback: RecordStore,
  ) {}
  private k(collection: string, id: string): string {
    return `${collection}::${id}`;
  }
  async put(collection: string, id: string, record: unknown): Promise<void> {
    const db = await this.dbp;
    if (!db) return this.fallback.put(collection, id, record);
    await tx(db, REC_STORE, "readwrite", (s) =>
      s.put({ _k: this.k(collection, id), _c: collection, value: record }),
    );
  }
  async get<T = unknown>(collection: string, id: string): Promise<T | undefined> {
    const db = await this.dbp;
    if (!db) return this.fallback.get<T>(collection, id);
    const row = await tx<{ value: T } | undefined>(db, REC_STORE, "readonly", (s) =>
      s.get(this.k(collection, id)),
    );
    return row?.value;
  }
  async all<T = unknown>(collection: string): Promise<T[]> {
    const db = await this.dbp;
    if (!db) return this.fallback.all<T>(collection);
    // Range-scoped on the `collection::` key prefix. This used to `getAll()` EVERY row of
    // EVERY collection — reading one queue job pulled the whole synced-template + mockup
    // corpus (hundreds of MB of decoded records) into memory and then filtered in JS.
    // `_c` is still checked because a collection name may itself contain "::".
    const rows =
      (await tx<{ _c: string; value: T }[]>(db, REC_STORE, "readonly", (s) =>
        s.getAll(prefixRange(`${collection}::`)),
      )) ?? [];
    return rows.filter((r) => r._c === collection).map((r) => r.value);
  }
  async remove(collection: string, id: string): Promise<void> {
    const db = await this.dbp;
    if (!db) return this.fallback.remove(collection, id);
    await tx(db, REC_STORE, "readwrite", (s) => s.delete(this.k(collection, id)));
  }
  async clear(collection: string): Promise<void> {
    const db = await this.dbp;
    if (!db) return this.fallback.clear(collection);
    // ONE transaction over the collection's key range. It used to open a separate readwrite
    // transaction PER ROW after a full-store scan, so a failure mid-way left the collection
    // half-cleared (and a big collection opened thousands of transactions).
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(REC_STORE, "readwrite");
      const req = t.objectStore(REC_STORE).openCursor(prefixRange(`${collection}::`));
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return; // done — `oncomplete` resolves once the tx commits
        const row = cursor.value as { _c?: string } | undefined;
        if (row?._c === collection) cursor.delete();
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
      t.oncomplete = () => resolve();
      t.onabort = () => reject(t.error ?? new Error("IndexedDB clear aborted"));
      t.onerror = () => reject(t.error ?? req.error ?? new Error("IndexedDB clear failed"));
    });
  }
}

export function createIdbLocalStore(): LocalStore {
  if (typeof indexedDB === "undefined") return createMemoryLocalStore();
  // Memory fallback, wired up front. A rejected `openDb()` (quota, private mode, a blocked
  // upgrade, a corrupt profile) used to propagate out of EVERY store call — an unhandled
  // rejection per call and total loss of persistence with no fallback at all, even though
  // the "IndexedDB unavailable" branch above already had one.
  const memory = createMemoryLocalStore();
  const dbp = openDb().catch(() => null);
  return {
    kv: new IdbKeyValueStore(dbp, memory.kv),
    records: new IdbRecordStore(dbp, memory.records),
  };
}
